import React, { useCallback, useEffect, useRef, useState } from "react";
import { askKnowledgeBase } from "./knowledgeAssistantService";

const OPEN_KEY = "mesg.kb.open";
const HISTORY_KEY = "mesg.kb.history";

const readOpen = () => {
  try {
    return sessionStorage.getItem(OPEN_KEY) === "1";
  } catch {
    return false;
  }
};
const readHistory = () => {
  try {
    const raw = sessionStorage.getItem(HISTORY_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/**
 * The Knowledge Base assistant — a docked, expandable panel available on every
 * "Manage ESG Reporting" page (rendered by ReportManagementShell).
 *
 *  - collapsed: a slim rail on the right edge of the content area
 *  - expanded on ≥ lg: a real right-hand column, page content shrinks to fit
 *  - expanded on < lg: slides over as a right-hand overlay
 *
 * Q&A is answered only from the Knowledge Base corpus, with sources shown per
 * answer. No audit trail — history is session-only (sessionStorage).
 */
export default function KnowledgeAssistantPanel() {
  const siteKey = process.env.REACT_APP_SITE_KEY;
  const [open, setOpen] = useState(readOpen);
  const [question, setQuestion] = useState("");
  const [history, setHistory] = useState(readHistory); // newest first
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    try {
      sessionStorage.setItem(OPEN_KEY, open ? "1" : "0");
    } catch {
      /* private mode */
    }
  }, [open]);

  useEffect(() => {
    try {
      sessionStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, 40)));
    } catch {
      /* private mode / quota */
    }
  }, [history]);

  useEffect(() => {
    if (open && scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [open, history]);

  const ask = useCallback(async () => {
    const q = question.trim();
    if (!q || loading) return;
    setLoading(true);
    setError("");
    try {
      const res = await askKnowledgeBase({ question: q }, { siteKey });
      setHistory((h) => [
        {
          id: `${Date.now()}`,
          question: q,
          answer: res.answer,
          refused: res.refused,
          refusalReason: res.refusalReason,
          citations: res.citations,
        },
        ...h,
      ]);
      setQuestion("");
    } catch (err) {
      setError(err?.message || "The assistant is unavailable right now.");
    } finally {
      setLoading(false);
    }
  }, [question, loading, siteKey]);

  const clear = () => {
    setHistory([]);
    setError("");
  };

  return (
    <div className={`rm-kb ${open ? "rm-kb--open" : "rm-kb--rail"}`}>
      <PanelStyle />
      <div className="rm-kb__dock">
        {!open ? (
          <button
            type="button"
            className="rm-kb__tab btn btn-dark"
            onClick={() => setOpen(true)}
            title="Open the Knowledge Base assistant"
          >
            <i className="bi bi-journal-text" />
            <span className="rm-kb__tab-label">Knowledge Base</span>
          </button>
        ) : (
          <div className="rm-kb__panel card border-0 shadow">
            <div className="card-header bg-dark text-white d-flex justify-content-between align-items-center py-2">
              <span className="fw-bold small text-uppercase" style={{ letterSpacing: "0.5px" }}>
                <i className="bi bi-journal-text me-2" />
                Knowledge Base
              </span>
              <div className="d-flex gap-2">
                {history.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-light border-0 p-0 px-1 extra-small"
                    onClick={clear}
                    title="Clear this session's questions"
                  >
                    Clear
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-light border-0 p-0 px-1"
                  onClick={() => setOpen(false)}
                  title="Collapse"
                >
                  <i className="bi bi-chevron-right" />
                </button>
              </div>
            </div>

            <div className="rm-kb__body" ref={scrollRef}>
              <p className="text-muted extra-small px-3 pt-3 mb-2">
                Answers come only from documents in the Knowledge Base, with the
                source shown for each point. Nothing here is recorded.
              </p>

              {error && (
                <div className="alert alert-warning py-2 small mx-3">{error}</div>
              )}

              {history.length === 0 && !error && (
                <div className="text-muted small px-3 py-4 text-center fst-italic">
                  Ask a question to get started.
                </div>
              )}

              {history.map((h) => (
                <div key={h.id} className="border-top px-3 py-3">
                  <div className="fw-bold small text-dark mb-1">
                    <i className="bi bi-person-fill me-1 text-secondary" />
                    {h.question}
                  </div>
                  {h.refused ? (
                    <div className="small text-danger mb-1">{h.refusalReason || "Not covered by the Knowledge Base."}</div>
                  ) : (
                    <div className="small text-dark mb-2" style={{ whiteSpace: "pre-wrap" }}>
                      {h.answer}
                    </div>
                  )}
                  {h.citations && h.citations.length > 0 && (
                    <div className="mt-1">
                      <div className="extra-small fw-bold text-uppercase text-muted mb-1">
                        Sources
                      </div>
                      {h.citations.map((c, i) => (
                        <div key={i} className="border rounded bg-light px-2 py-1 mb-1">
                          <div className="d-flex justify-content-between extra-small">
                            <span className="fw-bold text-dark">
                              [{i + 1}] {c.name || "Document"}
                            </span>
                            <span className="text-muted">
                              {c.section != null ? `§ ${c.section}` : ""}
                              {c.effectiveDate ? ` · eff. ${c.effectiveDate}` : ""}
                            </span>
                          </div>
                          {c.quote && (
                            <div className="extra-small text-secondary fst-italic mt-1">
                              &ldquo;{c.quote.length > 260 ? `${c.quote.slice(0, 260)}…` : c.quote}&rdquo;
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="card-footer bg-white border-top p-2">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  ask();
                }}
              >
                <textarea
                  className="form-control form-control-sm mb-2"
                  rows={2}
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      ask();
                    }
                  }}
                  placeholder="Ask the Knowledge Base…"
                  disabled={loading}
                />
                <button
                  type="submit"
                  className="btn btn-dark btn-sm w-100 fw-bold"
                  disabled={loading || !question.trim()}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Thinking…
                    </>
                  ) : (
                    "Ask"
                  )}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Scoped layout rules for the docked panel (kept with the component). */
function PanelStyle() {
  return (
    <style>{`
      .rm-shell { display: flex; align-items: flex-start; gap: 1rem; }
      .rm-shell__main { flex: 1 1 auto; min-width: 0; }
      .rm-kb { flex: 0 0 auto; align-self: stretch; }
      .rm-kb--rail { width: 44px; }
      .rm-kb--open { width: 380px; }
      .rm-kb__dock { position: sticky; top: 0.75rem; }
      .rm-kb__tab {
        writing-mode: vertical-rl; text-orientation: mixed;
        border-radius: 0.5rem 0 0 0.5rem; padding: 0.75rem 0.35rem;
        display: flex; align-items: center; gap: 0.5rem; font-weight: 700;
        font-size: 0.7rem; letter-spacing: 0.5px; text-transform: uppercase;
      }
      .rm-kb__tab-label { white-space: nowrap; }
      .rm-kb__panel { height: calc(100vh - 1.5rem); display: flex; flex-direction: column; }
      .rm-kb__body { flex: 1 1 auto; overflow-y: auto; }
      @media (max-width: 991.98px) {
        .rm-kb--open { width: 0; }
        .rm-kb--open .rm-kb__dock {
          position: fixed; inset: 0 0 0 auto; width: min(92vw, 380px);
          z-index: 1045; padding: 0;
        }
        .rm-kb--open .rm-kb__panel {
          height: 100vh; border-radius: 0;
          box-shadow: -10px 0 30px rgba(0,0,0,0.18);
        }
      }
    `}</style>
  );
}
